import React from 'react';
import {
  alpha,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import {
  CheckCircle,
  Circle,
  Forum,
  Flag,
  Groups,
  Interests,
  RocketLaunch,
  School,
} from '@mui/icons-material';

export type IntroRoadmapStep = {
  id: 'interests' | 'clubs' | 'courses' | 'friends';
  title: string;
  description: string;
  actionLabel: string;
  completed: boolean;
  path: string;
};

type IntroRoadmapDialogProps = {
  open: boolean;
  loading: boolean;
  steps: IntroRoadmapStep[];
  onClose: () => void;
  onGoToStep: (step: IntroRoadmapStep) => void;
};

const IntroRoadmapDialog: React.FC<IntroRoadmapDialogProps> = ({
  open,
  loading,
  steps,
  onClose,
  onGoToStep,
}) => {
  const doneCount = steps.filter((step) => step.completed).length;
  const progress = steps.length ? Math.round((doneCount / steps.length) * 100) : 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1.25,
          background: (theme) => `linear-gradient(140deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 55%, ${theme.palette.secondary.main} 100%)`,
          color: 'common.white',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.15 }}>
              Welcome to Dragonsframe
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.75, opacity: 0.9 }}>
              Quick roadmap to get your profile ready and start connecting.
            </Typography>
          </Box>
          <RocketLaunch sx={{ fontSize: 32, opacity: 0.9 }} />
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: { xs: 4.5, sm: 5 }, pb: 1.5 }}>

        <Box sx={{ mt: 0.5, mb: 2.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
            <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Flag fontSize="small" color="primary" />
              Progress
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              {doneCount}/{steps.length} completed
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 10,
              borderRadius: 999,
              backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.13),
              '& .MuiLinearProgress-bar': {
                borderRadius: 999,
              },
            }}
          />
        </Box>

        <List disablePadding>
          {steps.map((step) => (
            <ListItem
              key={step.id}
              disableGutters
              sx={{
                mb: 1.25,
                border: '1px solid',
                borderColor: step.completed ? 'success.light' : alpha('#1e3a8a', 0.2),
                backgroundColor: step.completed ? (theme) => alpha(theme.palette.success.main, 0.06) : (theme) => alpha(theme.palette.primary.main, 0.02),
                borderRadius: 2.25,
                px: 1.5,
                py: 1.2,
                alignItems: 'flex-start',
              }}
              secondaryAction={(
                <Button
                  variant={step.completed ? 'outlined' : 'contained'}
                  size="small"
                  onClick={() => onGoToStep(step)}
                  sx={{
                    mt: 0.15,
                    borderRadius: 999,
                    minWidth: 108,
                    fontWeight: 600,
                  }}
                >
                  {step.actionLabel}
                </Button>
              )}
            >
              <ListItemIcon sx={{ minWidth: 34, mt: 0.2 }}>
                {step.completed ? <CheckCircle color="success" fontSize="small" /> : <Circle color="disabled" fontSize="small" />}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {step.title}
                    </Typography>
                    {step.completed ? <Chip size="small" label="Done" color="success" /> : null}
                  </Box>
                }
                secondary={<Typography variant="body2" sx={{ mt: 0.35 }}>{step.description}</Typography>}
                sx={{ pr: 12 }}
              />
            </ListItem>
          ))}
        </List>

        {loading ? (
          <Typography variant="caption" color="text.secondary">
            Refreshing your roadmap status...
          </Typography>
        ) : null}

        <Box sx={{ mt: 2.2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip icon={<Interests />} label="Interests" variant="outlined" />
          <Chip icon={<Groups />} label="Clubs" variant="outlined" />
          <Chip icon={<School />} label="Courses" variant="outlined" />
          <Chip icon={<Forum />} label="Friends + Messages" variant="outlined" />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 0.5 }}>
        <Button onClick={onClose} variant="text" sx={{ fontWeight: 600 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IntroRoadmapDialog;
